/**
 * /sub-pay-apps — Subcontractor Pay Application Queue
 *
 * Tabs: "Awaiting Review" | "All" | "Paid"
 * Table: Sub Name, Project, App #, Period, Amount, Status, Waiver Status, Actions
 * "New Pay App" button opens a dialog to create a draft.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useConstructionPack } from "@/lib/useConstructionPack";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

import { api } from "@/lib/api";
import { subPayAppsQueryOptions, subPayAppKeys } from "@/lib/queries";
import type { SubPayAppListItem, SubPayAppStatus } from "@/lib/types";

export const Route = createFileRoute("/_auth/sub-pay-apps")({
  component: SubPayAppsPage,
});

// ─── Status chip helpers ──────────────────────────────────────────────────────

const STATUS_COLOR: Record<
  SubPayAppStatus,
  "default" | "primary" | "warning" | "success" | "error" | "secondary" | "info"
> = {
  draft: "default",
  submitted: "primary",
  under_review: "warning",
  approved: "success",
  rejected: "error",
  invoiced: "secondary",
  paid: "info",
  void: "default",
};

const STATUS_LABEL: Record<SubPayAppStatus, string> = {
  draft: "Draft",
  submitted: "Submitted",
  under_review: "Under Review",
  approved: "Approved",
  rejected: "Rejected",
  invoiced: "Invoiced",
  paid: "Paid",
  void: "Void",
};

function fmtMoney(v: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(v);
}

function fmtDate(s: string) {
  return new Date(s + "T00:00:00").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ─── New Pay App dialog ───────────────────────────────────────────────────────

interface NewPayAppDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}

function NewPayAppDialog({ open, onClose, onCreated }: NewPayAppDialogProps) {
  const queryClient = useQueryClient();
  const [subcontractId, setSubcontractId] = useState("");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");

  const createMutation = useMutation({
    mutationFn: (payload: {
      subcontract_id: string;
      period_start: string;
      period_end: string;
    }) => api("/api/sub-pay-apps", { method: "POST", body: payload }) as Promise<{ id: string }>,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: subPayAppKeys.all });
      onCreated(data.id);
    },
  });

  const handleSubmit = () => {
    if (!subcontractId || !periodStart || !periodEnd) return;
    createMutation.mutate({
      subcontract_id: subcontractId,
      period_start: periodStart,
      period_end: periodEnd,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Pay Application</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
        <TextField
          label="Subcontract ID"
          value={subcontractId}
          onChange={(e) => setSubcontractId(e.target.value)}
          helperText="Paste the subcontract UUID"
          fullWidth
        />
        <TextField
          label="Period Start"
          type="date"
          value={periodStart}
          onChange={(e) => setPeriodStart(e.target.value)}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
        <TextField
          label="Period End"
          type="date"
          value={periodEnd}
          onChange={(e) => setPeriodEnd(e.target.value)}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
        {createMutation.isError && (
          <Typography color="error" variant="body2">
            {String((createMutation.error as Error)?.message ?? "Error creating pay app")}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={createMutation.isPending || !subcontractId || !periodStart || !periodEnd}
        >
          {createMutation.isPending ? "Creating…" : "Create Draft"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function SubPayAppsPage() {
  const { enabled: constructionEnabled } = useConstructionPack();
  if (!constructionEnabled) return null;
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Determine filter based on tab
  const filterMap: Record<number, Record<string, string>> = {
    0: { status: "submitted" },
    1: {},
    2: { status: "paid" },
  };
  const filters = filterMap[tab] ?? {};

  const { data, isLoading } = useQuery(subPayAppsQueryOptions(filters));

  const handleCreated = (id: string) => {
    setDialogOpen(false);
    navigate({ to: "/sub-pay-apps/$payAppId", params: { payAppId: id } });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>
          Sub Pay Applications
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
        >
          New Pay App
        </Button>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Awaiting Review" />
        <Tab label="All" />
        <Tab label="Paid" />
      </Tabs>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Sub Name</TableCell>
              <TableCell>Project</TableCell>
              <TableCell align="center">App #</TableCell>
              <TableCell>Period End</TableCell>
              <TableCell align="right">Amount Due</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Waiver</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data ?? []).map((row: SubPayAppListItem) => (
              <TableRow
                key={row.id}
                hover
                sx={{ cursor: "pointer" }}
                onClick={() =>
                  navigate({ to: "/sub-pay-apps/$payAppId", params: { payAppId: row.id } })
                }
              >
                <TableCell>{row.sub_name}</TableCell>
                <TableCell>{row.project_name}</TableCell>
                <TableCell align="center">{row.application_no}</TableCell>
                <TableCell>{fmtDate(row.period_end)}</TableCell>
                <TableCell align="right">{fmtMoney(row.current_payment_due)}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={STATUS_LABEL[row.status]}
                    color={STATUS_COLOR[row.status]}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  {row.conditional_waiver_signed ? (
                    <Chip label="Signed" color="success" size="small" />
                  ) : (
                    <Chip label="Pending" color="default" size="small" />
                  )}
                </TableCell>
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                  <Button
                    size="small"
                    startIcon={<PictureAsPdfIcon />}
                    href={`/api/sub-pay-apps/${row.id}/pdf`}
                    target="_blank"
                  >
                    PDF
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {(data ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No pay applications found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <NewPayAppDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={handleCreated}
      />
    </Box>
  );
}
